/** @format */

import React, {useContext, useEffect} from 'react';

import {Select, Tooltip, Checkbox, Input, IconButton} from '@sttark/design-system';

import {QuoteContext} from 'frontier-quoter';
import {
    COLOR_BLACK_ONLY,
    COLOR_WHITE_ONLY,
    COLOR_CAPATIBILITY_MATRIX,
    COLOR_DOUBLE_SIDED,
    SUBSTRATE_BLACK_VELLUM,
    PRODUCT_TYPE_LABEL,
    PRODUCT_TYPE_CARTON,
    CARTON_BLACK_VELLUM,
    ROLL_SIZE_TYPE_DIAMETER,
    ROLL_SIZE_TYPE_QTY,
    CARTON_BRIGHT_WHITE_PAPER,
    CARTON_WHITE_PAPER_C2S,
    CARTON_WHITE_PAPER,
    SUBSTRATE_SILVER_METALLIC_PLASTIC,
    SUBSTRATE_RAINBOW_HOLOGRAM_PLASTIC,
    CARTON_METALLIC_PAPER,
    CARTON_HOLOGRAPHIC,
    CARTON_UNLAMINATED_WHITE_PAPER,
    EMBOSSING_TYPE_ALL,
    EMBOSSING_TYPE_UNIQUE,
    EMBOSSING_TYPE_NONE,
    PROOF_TYPE_ELECTRONIC,
    PROOF_TYPE_PRINTED
} from 'frontier-constants';
import {ConditionalButton, If} from 'frontier-utilities';
import {CardSelect} from '@sttark/design-system';
import Section from 'frontier/ds/quoter/section';

export default function AdditionalOptions({errors}) {
    const ctx = useContext(QuoteContext);
    const order = ctx.order;

    const canDoubleSided = COLOR_CAPATIBILITY_MATRIX[COLOR_DOUBLE_SIDED].includes(
        order.substrate_id
    );

    // Also in DuplexFrontBackOption
    const canBlackInkOnly = COLOR_CAPATIBILITY_MATRIX[COLOR_BLACK_ONLY].includes(
        order.substrate_id
    );

    const canReversePrint = COLOR_CAPATIBILITY_MATRIX[COLOR_DOUBLE_SIDED].includes(
        order.substrate_id
    );
    // Also in DuplexFrontBackOption
    const canWhiteInkOnly = COLOR_CAPATIBILITY_MATRIX[COLOR_WHITE_ONLY].includes(
        order.substrate_id
    );

    const canHighOpacityWhite =
        ![
            CARTON_BLACK_VELLUM,
            CARTON_UNLAMINATED_WHITE_PAPER,
            SUBSTRATE_SILVER_METALLIC_PLASTIC,
            CARTON_METALLIC_PAPER,
            SUBSTRATE_RAINBOW_HOLOGRAM_PLASTIC,
            CARTON_HOLOGRAPHIC
        ].includes(order.substrate_id) &&
        COLOR_CAPATIBILITY_MATRIX[COLOR_WHITE_ONLY].includes(order.substrate_id) &&
        order.color_id !== COLOR_BLACK_ONLY &&
        !order.is_high_opacity_black;

    const canHighOpacityBlack =
        ![CARTON_BLACK_VELLUM, CARTON_UNLAMINATED_WHITE_PAPER, SUBSTRATE_BLACK_VELLUM].includes(
            order.substrate_id
        ) &&
        order.color_id !== COLOR_WHITE_ONLY &&
        !order.is_high_opacity_white;

    const canSpecialRollQty = !errors.some(
        e => e.category === 'total_qty' || e.category === 'size'
    );

    const canMoistureResistant =
        [CARTON_BRIGHT_WHITE_PAPER, CARTON_WHITE_PAPER_C2S, CARTON_WHITE_PAPER].indexOf(
            order.substrate_id
        ) > -1;
    // If duplex and cs2, you cannot modify this
    // Because duplex requires c2s
    const disableEditMoistureResistant =
        order.substrate_id === CARTON_WHITE_PAPER_C2S && order.is_duplex;

    useEffect(() => {
        applyAccountDefaults();
    }, []);

    const handleColorChange = (checked, color_id) => {
        if (checked) {
            ctx.handleChange({order: {color_id: {$set: color_id}}});
        } else if (!checked && order.color_id === color_id) {
            ctx.handleChange({order: {color_id: {$set: null}}});
        }
    };

    const handleToggle = (field, secondary = {field: false, value: false}) => {
        const isEnabled = ctx.order[field] || false;
        const updateTarget = {
            order: {
                [field]: {$set: !isEnabled}
            }
        };

        if (secondary.field) {
            updateTarget.order[secondary.field] = {$set: secondary.value};
        }

        // If we're enabling embossing for a carton order and the proof type is printed proof,
        // we need to check if it would become printed proof only and change to digital proof if so.
        if (
            field === 'is_embossing' &&
            !isEnabled &&
            ctx.order.product_type_id === PRODUCT_TYPE_CARTON &&
            ctx.order.proof_type_id === PROOF_TYPE_PRINTED
        ) {
            // Check if this would be printed proof only (design qty equals total qty)
            let isPrintedProofOnlyPossible =
                ctx.designs_orders.filter(design => design.qty !== 1).length === 0;

            if (ctx.designs_orders.length === 0) {
                // Skipping Artwork Upload
                isPrintedProofOnlyPossible =
                    ctx.order.total_qty === ctx.order.design_qty && ctx.order.total_qty > 0;
            }

            // If it would be printed proof only, change to digital proof
            if (isPrintedProofOnlyPossible) {
                updateTarget.order.proof_type_id = {$set: PROOF_TYPE_ELECTRONIC};
                updateTarget.order.is_flat_printed_proof = {$set: 0};
            }
        }

        ctx.handleUpdate(updateTarget);
    };

    const maxRollsError = errors.find(e => e.category === 'max_rolls');
    const maxQtyError = errors.find(e => e.category === 'max_qty');
    const maxDiameterError = errors.find(e => e.category === 'max_diameter');
    const designQtyError = errors.find(e => e?.subcategory === 'design_qty');

    // If company level roll requirements are set
    let accountRollOptionsEnabled = false;
    let accountRollDiameterMax = '';
    let accountRollQtyMax = '';
    let accountIsFewestPossibleRolls = false;
    if (
        ctx.cart?.user?.company_entity?.roll_max ||
        ctx.cart?.user?.company_entity?.is_fewest_possible_rolls
    ) {
        accountRollOptionsEnabled = true;
        accountRollDiameterMax =
            ctx.cart.user.company_entity.roll_type_id === ROLL_SIZE_TYPE_DIAMETER
                ? ctx.cart.user.company_entity.roll_max
                : '';
        accountRollQtyMax =
            ctx.cart.user.company_entity.roll_type_id === ROLL_SIZE_TYPE_QTY
                ? ctx.cart.user.company_entity.roll_max
                : '';
        accountIsFewestPossibleRolls = ctx.cart.user.company_entity.is_fewest_possible_rolls;
    }

    const applyAccountDefaults = () => {
        if (
            !order.is_account_roll_defaults_applied &&
            accountRollOptionsEnabled &&
            !designQtyError
        ) {
            ctx.handleUpdate({
                order: {
                    is_special_roll_qty: {$set: true},
                    is_account_roll_defaults_applied: {$set: true},
                    is_fewest_possible_rolls: {$set: accountIsFewestPossibleRolls},
                    roll_max: {$set: accountRollQtyMax || accountRollDiameterMax},
                    roll_diameter_max: {$set: accountRollDiameterMax || ''},
                    roll_qty_max: {$set: accountRollQtyMax || ''}
                }
            });
        }
        if (designQtyError && order.is_special_roll_qty) {
            ctx.handleUpdate({
                order: {
                    is_special_roll_qty: {$set: false},
                    is_account_roll_defaults_applied: {$set: false}
                }
            });
        }
    };

    const handleUpdateSpecialRollToggle = field => {
        const isEnabled = canSpecialRollQty ? ctx.order[field] || false : false;
        ctx.handleUpdate({
            order: {
                [field]: {$set: !isEnabled}
            }
        });
    };

    const handleUpdateRollToggle = field => {
        const isEnabled = ctx.order[field] || false;
        ctx.handleUpdate({
            order: {
                [field]: {$set: !isEnabled},
                is_account_roll_defaults_applied: {$set: true}
            }
        });
    };

    const handleUpdateIsMoistureResistantToggle = () => {
        const isEnabled = ctx.order.substrate_id === CARTON_WHITE_PAPER_C2S || false;
        let substrateId = CARTON_BRIGHT_WHITE_PAPER;
        if (!isEnabled) {
            substrateId = CARTON_WHITE_PAPER_C2S;
        }

        ctx.handleUpdate({
            order: {
                substrate_id: {$set: substrateId}
            }
        });
    };

    const blankInfo =
        order.product_type_id === PRODUCT_TYPE_LABEL
            ? 'High quality label material die cut on a roll, perfect for in-house printing.'
            : 'Paperboard cut, folded, and glued; perfect for test fitting your product.';

    const embossingInfo = `We are pleased to offer embossing on our custom cartons. Accentuate your unique product with a touch of class`;

    return (
        <>
            <div className="row">
                <div className="col-md-6">
                    <Checkbox
                        disabled={!canBlackInkOnly}
                        checked={order.color_id === COLOR_BLACK_ONLY}
                        onChange={b => handleColorChange(b, COLOR_BLACK_ONLY)}>
                        Black Ink Only
                        <Tooltip content="An economical print mode where artwork is converted to grayscale and only black ink is used.">
                            <IconButton secondary small icon="info" color="black" />
                        </Tooltip>
                    </Checkbox>
                    <Checkbox
                        disabled={!canWhiteInkOnly}
                        checked={order.color_id === COLOR_WHITE_ONLY}
                        onChange={b => handleColorChange(b, COLOR_WHITE_ONLY)}>
                        White Ink Only
                        <Tooltip content="For designs that only require white ink. No other colors are utilized.">
                            <IconButton secondary small icon="info" />
                        </Tooltip>
                    </Checkbox>
                    {order.product_type_id === PRODUCT_TYPE_LABEL && (
                        <Checkbox
                            disabled={!canDoubleSided}
                            checked={order.color_id === COLOR_DOUBLE_SIDED}
                            onChange={b => handleColorChange(b, COLOR_DOUBLE_SIDED)}>
                            Double-Sided
                            <Tooltip content="Allows you to print text or a design that can be seen from the back of your label. Only available on clear materials.">
                                <IconButton secondary small icon="info" />
                            </Tooltip>
                        </Checkbox>
                    )}
                    <Checkbox
                        checked={order.is_blank}
                        onChange={handleToggle.bind(this, 'is_blank')}>
                        {ctx.order.product_type_id === PRODUCT_TYPE_LABEL
                            ? 'Blank Labels'
                            : 'Blank Cartons'}
                        <Tooltip content={blankInfo}>
                            <IconButton secondary small icon="info" />
                        </Tooltip>
                    </Checkbox>
                    <If condition={order.product_type_id === PRODUCT_TYPE_CARTON}>
                        <Checkbox
                            checked={order.is_embossing}
                            onChange={handleToggle.bind(this, 'is_embossing', {
                                field: 'embossing_type_id',
                                value: order.is_embossing ? EMBOSSING_TYPE_NONE : EMBOSSING_TYPE_ALL
                            })}>
                            Embossing
                            <Tooltip content={embossingInfo}>
                                <IconButton secondary small icon="info" />
                            </Tooltip>
                            <sup style={{color: 'red'}}>New!</sup>
                        </Checkbox>
                    </If>
                </div>
                <div className="col-md-6">
                    {order.product_type_id === PRODUCT_TYPE_LABEL && (
                        <>
                            <ConditionalButton enabled={!designQtyError}>
                                <Checkbox
                                    disabled={!canSpecialRollQty}
                                    checked={order.is_special_roll_qty}
                                    onChange={handleUpdateSpecialRollToggle.bind(
                                        this,
                                        'is_special_roll_qty'
                                    )}>
                                    Qty on Roll/Roll Diameter
                                    <Tooltip content="Indicate the desired number of labels per roll and/or the desired size of the finished label roll.">
                                        <IconButton secondary small icon="info" />
                                    </Tooltip>
                                </Checkbox>
                                <Checkbox disabled={true} checked={false}>
                                    Qty on Roll/Roll Diameter
                                    <Tooltip content="Indicate the desired number of labels per roll and/or the desired size of the finished label roll.">
                                        <IconButton secondary small icon="info" />
                                    </Tooltip>
                                </Checkbox>
                            </ConditionalButton>
                        </>
                    )}
                    <Checkbox
                        disabled={!canHighOpacityBlack}
                        checked={order.is_high_opacity_black}
                        onChange={handleToggle.bind(this, 'is_high_opacity_black')}>
                        High Opacity Black
                        <Tooltip content="Black ink will be doubled, resulting in a more opaque layer of ink and a bolder print.">
                            <IconButton secondary small icon="info" />
                        </Tooltip>
                    </Checkbox>
                    <Checkbox
                        disabled={!canHighOpacityWhite}
                        checked={order.is_high_opacity_white}
                        onChange={handleToggle.bind(this, 'is_high_opacity_white')}>
                        High Opacity White
                        <Tooltip content="White ink will be doubled, resulting in a more opaque layer of ink and a bolder print.">
                            <IconButton secondary small icon="info" />
                        </Tooltip>
                    </Checkbox>
                    {order.product_type_id === PRODUCT_TYPE_LABEL && (
                        <Checkbox
                            disabled={!canReversePrint}
                            checked={order.is_reverse_print}
                            onChange={handleToggle.bind(this, 'is_reverse_print')}>
                            Reverse Printing
                            <Tooltip content="Backwards print on clear materials for application on clear surfaces like windows.">
                                <IconButton secondary small icon="info" />
                            </Tooltip>
                        </Checkbox>
                    )}
                    {order.product_type_id !== PRODUCT_TYPE_LABEL && (
                        <Checkbox
                            checked={order.is_design_in_progress}
                            onChange={handleToggle.bind(this, 'is_design_in_progress')}>
                            Resize Artwork
                            <Tooltip content="Your artwork will be adjusted to fit the size you entered here. You will receive a digital proof prior to production.">
                                <IconButton secondary small icon="info" />
                            </Tooltip>
                        </Checkbox>
                    )}
                    {order.product_type_id !== PRODUCT_TYPE_LABEL && (
                        <Checkbox
                            disabled={!canMoistureResistant || disableEditMoistureResistant}
                            checked={parseInt(order.substrate_id) === CARTON_WHITE_PAPER_C2S}
                            onChange={handleUpdateIsMoistureResistantToggle}>
                            Moisture & Oil Resistant Coating
                            <Tooltip content="Use for products like candles, soap, or essential oils to improve resistance to moisture & oils.">
                                <IconButton secondary small icon="info" />
                            </Tooltip>
                        </Checkbox>
                    )}
                </div>
            </div>

            {order.is_embossing && (
                <>
                    <h6 className="mt-6">Embossing Options</h6>
                    <CardSelect
                        options={[
                            {
                                label: 'Identical Embossing',
                                value: EMBOSSING_TYPE_ALL,
                                description: 'Each carton design has the same embossed element.'
                            },
                            {
                                label: 'Unique Embossing',
                                value: EMBOSSING_TYPE_UNIQUE,
                                description: 'All carton designs have unique embossed elements.'
                            }
                        ]}
                        value={parseInt(order.embossing_type_id)}
                        onChange={v => {
                            ctx.handleChange({
                                order: {embossing_type_id: {$set: v}}
                            });
                        }}
                    />
                </>
            )}

            {order.is_special_roll_qty &&
                order.product_type_id === PRODUCT_TYPE_LABEL &&
                !designQtyError && (
                    <>
                        <h6>Label Roll Options</h6>
                        {accountRollOptionsEnabled && (
                            <p>
                                <IconButton
                                    secondary
                                    large
                                    className="color-accent-tertiary"
                                    color="#e5b200"
                                    icon="warning"
                                />{' '}
                                These values are pre-populated based on your chosen Production
                                Presets. To edit them globally, please visit your{' '}
                                <a href="/account/users/account-details" target="_blank">
                                    Accounts
                                </a>{' '}
                                page.
                            </p>
                        )}
                        <div className="row">
                            <div className="col-12">
                                <Checkbox
                                    checked={
                                        !order.is_account_roll_defaults_applied
                                            ? accountIsFewestPossibleRolls
                                            : order.is_fewest_possible_rolls
                                    }
                                    onChange={handleUpdateRollToggle.bind(
                                        this,
                                        'is_fewest_possible_rolls'
                                    )}
                                    error={maxRollsError?.message}>
                                    Produce fewest possible rolls
                                </Checkbox>
                            </div>
                            <div className="col-12">
                                <Input
                                    type="number"
                                    label="Maximum labels per roll (+0/-10%)"
                                    value={
                                        !order.is_account_roll_defaults_applied
                                            ? accountRollQtyMax
                                            : order.roll_qty_max
                                    }
                                    error={maxQtyError?.message}
                                    onChange={value =>
                                        ctx.handleUpdateRollQty(parseFloat(value), 'roll_qty_max')
                                    }
                                    disabled={
                                        order.roll_diameter_max && order.roll_diameter_max !== ''
                                    }
                                />
                            </div>
                            <div className="col-12">
                                <Input
                                    type="number"
                                    suffix='"'
                                    label="Maximum Roll Diameter (+0/-10%)"
                                    value={
                                        !order.is_account_roll_defaults_applied
                                            ? accountRollDiameterMax
                                            : order.roll_diameter_max
                                    }
                                    error={maxDiameterError?.message}
                                    onChange={value => {
                                        value = parseInt(value);
                                        value = value < 4 ? 4 : value;
                                        ctx.handleUpdateRollQty(value, 'roll_diameter_max');
                                    }}
                                    disabled={order.roll_qty_max && order.roll_qty_max !== ''}
                                />
                            </div>
                        </div>
                    </>
                )}
        </>
    );
}
